import axios from "axios";
import CreateFormFieldRequest from "../models/serviceModels/CreateFormFieldRequest";
import CreateFormInstanceRequest from "../models/serviceModels/CreateFormInstanceRequest";
import CreateFormRequest from "../models/serviceModels/CreateFormRequest";
import DeleteFieldRequest from "../models/serviceModels/DeleteFieldRequest";
import DeleteFormRequest from "../models/serviceModels/DeleteFormRequest";
import Form from "../models/serviceModels/Form";
import FormField from "../models/serviceModels/FormField";
import FormInstance from "../models/serviceModels/FormInstance";
import LatestFormInstance from "../models/serviceModels/LatestFormInstance";
import NewFormVerionResponse from "../models/serviceModels/NewFormVersionResponse";
import PagedData from "../models/serviceModels/PagedData";
import ReorderFieldRequest from "../models/serviceModels/ReorderFieldRequest";
import SearchFormsInstancesRequest from "../models/serviceModels/SearchFormsInstancesRequest";
import UpdateFieldRequest from "../models/serviceModels/UpdateFieldRequest";
import UpdateFormInstanceRequest from "../models/serviceModels/UpdateFormInstanceRequest";
import UpdateFormRequest from "../models/serviceModels/UpdateFormRequest";
import MakeFormPublicResponse from "../models/serviceModels/MakeFormPublicResponse";
import MakeFormPrivateResponse from "../models/serviceModels/MakeFormPrivateResponse";
import RegeneratePublicIdResponse from "../models/serviceModels/RegeneratePublicIdResponse";
import CreatePublicFormInstanceRequest from "../models/serviceModels/CreatePublicFormInstanceRequest";
import MarkPublicSubmissionProcessedRequest from "../models/serviceModels/MarkPublicSubmissionProcessedRequest";
import MarkPublicSubmissionUnprocessedRequest from "../models/serviceModels/MarkPublicSubmissionUnprocessedRequest";
import GetPublicSubmissionsSummaryResponse from "../models/serviceModels/GetPublicSubmissionsSummaryResponse";
import GetPublicSubmissionsResultsRequest from "../models/serviceModels/GetPublicSubmissionsResultsRequest";
import PublicIntakeSubmission from "../models/serviceModels/PublicIntakeSubmission";
import {
    AttachPublicSubmissionResponse,
    CreatePublicSubmissionFollowUpResponse,
    PublicSubmissionFollowUpIdentity,
    PublicSubmissionFollowUpIdentityRequirements,
    PublicSubmissionFollowUpPublicView,
    PublicSubmissionFollowUpsResponse
} from "../models/serviceModels/PublicSubmissionFollowUp";
import { removeSuffix } from "./cacheService";
import ParticipantPublicSubmission from "../models/serviceModels/ParticipantPublicSubmission";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const getUnassignForms = async (): Promise<Form[]> => {
    const result = await axios.get(`${baseUrl}/forms/unassigned`);
    return result.data;
};

const getClientForm = async (): Promise<Form> => {
    const result = await axios.get(`${baseUrl}/forms/client`);
    return result.data;
};

const getClientFormInstance = async (id: string): Promise<FormInstance> => {
    const result = await axios.get(`${baseUrl}/forms/client/instances/client/${id}`);
    return result.data;
};

const getLatestForm = async (formId: string, includeStats = false): Promise<Form> => {
    const result = await axios.get(`${baseUrl}/forms/${formId}/latest?includeStats=${includeStats ? 'true' : 'false'}`);
    return result.data;
};

const getForm = async (formId: string, versionId: string, includeStats = false): Promise<Form> => {
    const result = await axios.get(`${baseUrl}/forms/${formId}/versions/${versionId}?includeStats=${includeStats ? 'true' : 'false'}`);
    return result.data;
};

const getPublicForm = async (publicFormId: string): Promise<Form> => {
    const result = await axios.get(`${baseUrl}/forms/public/${publicFormId}`);
    return result.data;
};

const deleteFormInstance = async (formId: string, instanceId: string): Promise<void> => {
    await axios.delete(`${baseUrl}/forms/${formId}/instances/${instanceId}`);
};

const getFormInstance = async (formId: string, instanceId: string): Promise<FormInstance> => {
    const result = await axios.get(`${baseUrl}/forms/${formId}/instances/${instanceId}`);
    return result.data;
};

const getForms = async (includeDeactivated = false, types?: number[], includeDrafts = true): Promise<Form[]> => {
    const result = await axios.get(`${baseUrl}/forms?includeDeactivated=${includeDeactivated}&types=${types ? types.join(',') : ''}&includeDrafts=${includeDrafts}`);
    return result.data;
};

const createForm = async (request: CreateFormRequest): Promise<Form> => {
    const result = await axios.post(`${baseUrl}/forms`, request);
    return result.data;
};

const createFormVersion = async (id: string): Promise<NewFormVerionResponse> => {
    const result = await axios.post(`${baseUrl}/forms/${id}/versions`);
    return result.data;
}

const makeFormPublic = async (id: string): Promise<MakeFormPublicResponse> => {
    const result = await axios.post(`${baseUrl}/forms/${id}/public`);
    return result.data;
}

const makeFormPrivate = async (id: string): Promise<MakeFormPrivateResponse> => {
    const result = await axios.post(`${baseUrl}/forms/${id}/private`);
    return result.data;
}

const regeneratePublicId = async (id: string): Promise<RegeneratePublicIdResponse> => {
    const result = await axios.post(`${baseUrl}/forms/${id}/public-id/regenerate`);
    return result.data;
}

const createFormField = async (request: CreateFormFieldRequest): Promise<FormField> => {
    const result = await axios.post(`${baseUrl}/forms/${request.formId}/versions/${request.versionId}/fields`, request);
    return result.data;
};

const deleteFormDraft = async (request: DeleteFormRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/forms/${request.formId}/versions/${request.versionId}`);
};

const deleteForm = async (formId: string): Promise<void> => {
    await axios.delete(`${baseUrl}/forms/${formId}`);
    removeSuffix('-search-fields-v5');
}

const activateForm = async (formId: string): Promise<void> => {
    await axios.post(`${baseUrl}/forms/${formId}/activate`);
    removeSuffix('-search-fields-v5');
};

const deactivateForm = async (formId: string): Promise<void> => {
    await axios.post(`${baseUrl}/forms/${formId}/deactivate`);
    removeSuffix('-search-fields-v5');
};

const updateForm = async (request: UpdateFormRequest): Promise<Form> => {
    const result = await axios.put(`${baseUrl}/forms/${request.id}`, {
        name: request.name,
        thankYouMessage: request.thankYouMessage,
        useDefaultThankYouMessage: request.useDefaultThankYouMessage
    });
    removeSuffix('-search-fields-v5');

    return result.data;
};

const createFormInstance = async (request: CreateFormInstanceRequest): Promise<FormInstance> => {
    const result = await axios.post(`${baseUrl}/forms/${request.formId}/versions/${request.versionId}/instances`, {
        ...request
    });

    return result.data;
};

const createPublicFormInstance = async (request: CreatePublicFormInstanceRequest): Promise<void> => {
    await axios.post(`${baseUrl}/forms/public/${request.publicFormId}/instance`, {
        ...request
    });
};

const markPublicSubmissionProcessed = async (request: MarkPublicSubmissionProcessedRequest): Promise<void> => {
    await axios.post(`${baseUrl}/forms/instance/${request.formInstanceId}/processed`);
};

const markPublicSubmissionUnprocessed = async (request: MarkPublicSubmissionUnprocessedRequest): Promise<void> => {
    await axios.post(`${baseUrl}/forms/instance/${request.formInstanceId}/unprocessed`, {
        intendToDelete: request.intendToDelete
    });
};

const getPublicSubmissionsSummary = async (fromDate?: string): Promise<GetPublicSubmissionsSummaryResponse> => {
    const query = [];
    query.push(`from=${fromDate}`);
    const response = await axios.get(`${baseUrl}/forms/submissions/public/summary?${query.join("&")}`);
    return {
        ...response.data,
        results: response.data.results.map((result: {
            status: string;
            allTimeCount: number | string;
            filteredCount: number | string;
        }) => ({
            ...result,
            allTimeCount: Number(result.allTimeCount),
            filteredCount: Number(result.filteredCount)
        }))
    };
};

const getPublicSubmissionsResults = async (request: GetPublicSubmissionsResultsRequest): Promise<PagedData<PublicIntakeSubmission>> => {
    const query = [];
    query.push(`take=${request.take || 10}`);
    query.push(`skip=${request.skip || 0}`);
    query.push(`filters=${request.filters.join(',')}`);
    query.push(`sortBy=${request.sortBy}`);
    query.push(`from=${request.fromDate || ''}`);
    query.push(`sortDirection=${request.sortDirection}`);

    const queryString = query.join('&');

    const response = await axios.get(`${baseUrl}/forms/submissions/public/results?${queryString}`);

    return response.data;
};

const getPublicSubmissionFollowUps = async (formInstanceId: string): Promise<PublicSubmissionFollowUpsResponse> => {
    const response = await axios.get(`${baseUrl}/forms/submissions/public/${formInstanceId}/follow-ups`);
    return response.data;
};

const getClientPublicSubmissions = async (clientId: string): Promise<ParticipantPublicSubmission[]> => {
    const response = await axios.get(`${baseUrl}/forms/clients/${clientId}/public-submissions`);
    return response.data;
};

const createPublicSubmissionFollowUp = async (formInstanceId: string, requestText: string): Promise<CreatePublicSubmissionFollowUpResponse> => {
    const response = await axios.post(`${baseUrl}/forms/submissions/public/${formInstanceId}/follow-ups`, {
        requestText
    });
    return response.data;
};

const reactivatePublicSubmissionFollowUp = async (formInstanceId: string, followUpId: string): Promise<void> => {
    await axios.post(`${baseUrl}/forms/submissions/public/${formInstanceId}/follow-ups/${followUpId}/reactivate`);
};

const cancelPublicSubmissionFollowUp = async (formInstanceId: string, followUpId: string): Promise<void> => {
    await axios.post(`${baseUrl}/forms/submissions/public/${formInstanceId}/follow-ups/${followUpId}/cancel`);
};

const attachPublicSubmission = async (formInstanceId: string, clientId: string): Promise<AttachPublicSubmissionResponse> => {
    const response = await axios.post(`${baseUrl}/forms/submissions/public/${formInstanceId}/attach`, {
        clientId
    });
    return response.data;
};

const getPublicSubmissionFollowUp = async (token: string, identity: PublicSubmissionFollowUpIdentity): Promise<PublicSubmissionFollowUpPublicView> => {
    const response = await axios.post(`${baseUrl}/forms/public/submissions/follow-up/verify`, {
        publicFollowUpToken: token,
        identity
    });
    return response.data;
};

const getPublicSubmissionFollowUpIdentityRequirements = async (token: string): Promise<PublicSubmissionFollowUpIdentityRequirements> => {
    const response = await axios.post(`${baseUrl}/forms/public/submissions/follow-up/identity-requirements`, {
        publicFollowUpToken: token
    });
    return response.data;
};

const submitPublicSubmissionFollowUpResponse = async (token: string, note: string, files: string[]): Promise<void> => {
    await axios.post(`${baseUrl}/forms/public/submissions/follow-up/response`, {
        note,
        files
    }, {
        headers: { 'x-public-follow-up-token': token }
    });
};

const getLatestFormInstances = async (formTypeId: number): Promise<LatestFormInstance[]> => {
    const result = await axios.get(`${baseUrl}/forms/instances/recent?type=${formTypeId}`);
    return result.data;
};

const publishFormVersion = async (formId: string, versionId: string): Promise<void> => {
    await axios.post(`${baseUrl}/forms/${formId}/versions/${versionId}/publish`);
}

const updateField = async (request: UpdateFieldRequest): Promise<FormField> => {
    const result = await axios.put(`${baseUrl}/forms/${request.formId}/versions/${request.versionId}/fields/${request.id}`, { ...request });
    return result.data;
};

const deletedField = async (request: DeleteFieldRequest): Promise<void> => {
    await axios.delete(`${baseUrl}/forms/${request.formId}/versions/${request.versionId}/fields/${request.id}`);
};

const reorderField = async (request: ReorderFieldRequest): Promise<FormField[]> => {
    const result = await axios.post(`${baseUrl}/forms/${request.formId}/versions/${request.versionId}/fields/reorder`, {
        fields: request.fields
    });
    return result.data;
};

const updateFormInstance = async (request: UpdateFormInstanceRequest): Promise<FormInstance> => {
    const result = await axios.put(`${baseUrl}/forms/${request.formId}/instances/${request.instanceId}`, request);
    return result.data
};

const searchFormsInstances = async (request?: SearchFormsInstancesRequest): Promise<PagedData<FormInstance>> => {
    const queryStringParts = [
        `id=${request?.formId || ''}`,
        `skip=${request?.skip || 0}`,
        `take=${request?.take || 10}`,
        `type=${request?.formTypeId || ''}`,
        `client=${request?.clientId || ''}`
    ];

    const queryString = `?${queryStringParts.join('&')}`;
    const result = await axios.get(`${baseUrl}/forms/instances/search${queryString}`);
    return result.data;
};

export {
    getPublicForm,
    getUnassignForms,
    getClientForm,
    getClientFormInstance,
    getForms,
    createForm,
    createFormVersion,
    createFormField,
    deleteFormDraft,
    deleteFormInstance,
    activateForm,
    deleteForm,
    deactivateForm,
    updateForm,
    getForm,
    createFormInstance,
    getLatestFormInstances,
    updateField,
    publishFormVersion,
    deletedField,
    reorderField,
    getFormInstance,
    updateFormInstance,
    searchFormsInstances,
    getLatestForm,
    makeFormPublic,
    makeFormPrivate,
    regeneratePublicId,
    createPublicFormInstance,
    markPublicSubmissionProcessed,
    markPublicSubmissionUnprocessed,
    getPublicSubmissionsResults,
    getPublicSubmissionsSummary,
    getPublicSubmissionFollowUps,
    getClientPublicSubmissions,
    createPublicSubmissionFollowUp,
    reactivatePublicSubmissionFollowUp,
    cancelPublicSubmissionFollowUp,
    attachPublicSubmission,
    getPublicSubmissionFollowUp,
    getPublicSubmissionFollowUpIdentityRequirements,
    submitPublicSubmissionFollowUpResponse
}
